import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, FileVideo, Upload, X, AlertCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { MOVE_TYPES, useLanguage } from '../i18n';
import { ProcessingLoader } from '../components/ProcessingLoader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const MAX_DURATION = 15.5;

/* ---------------------------------------------------------------------------
 * Presentational sub-components (extracted to keep Analyze lean)
 * ------------------------------------------------------------------------- */

const ClientError = ({ text }) => (
  <div className="flex items-center gap-2 text-xs text-[#E76F7B] bg-[rgba(231,111,123,0.07)] border border-[rgba(231,111,123,0.25)] rounded-[14px] px-3 py-2.5" data-testid="analyze-client-error">
    <AlertCircle size={14} className="shrink-0" /> {text}
  </div>
);

const ErrorCard = ({ serverError, onRetry, t }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="ss-card p-6 text-center space-y-3" data-testid="analyze-error-card">
    <span className="inline-flex w-12 h-12 rounded-[16px] bg-[rgba(231,111,123,0.1)] border border-[rgba(231,111,123,0.3)] items-center justify-center">
      <AlertCircle size={22} className="text-[#E76F7B]" />
    </span>
    <div>
      <h2 className="font-display font-bold text-white">{t('analysisFailedTitle')}</h2>
      <p className="text-xs text-white/45 font-mono mt-1 break-words">{serverError}</p>
    </div>
    <button onClick={onRetry} data-testid="analyze-retry-button" className="ss-btn-glass h-11 px-6 text-sm font-medium">
      {t('tryAgain')}
    </button>
  </motion.div>
);

const PreviewCard = ({ previewUrl, file, duration, onRemove }) => (
  <div className="ss-card p-3">
    <div className="relative rounded-[14px] overflow-hidden bg-black aspect-video">
      <video src={previewUrl} controls playsInline className="w-full h-full object-contain" data-testid="analyze-preview-video" />
      <button
        onClick={onRemove}
        data-testid="analyze-remove-video"
        className="absolute top-2 right-2 w-8 h-8 rounded-[14px] bg-black/70 border border-white/20 flex items-center justify-center"
      >
        <X size={15} className="text-white" />
      </button>
    </div>
    <div className="flex items-center justify-between px-1 pt-2.5 pb-0.5">
      <span className="font-mono text-[11px] text-white/45 truncate max-w-[55%]">{file?.name}</span>
      {duration && (
        <span className="flex items-center gap-1 font-mono text-[11px] text-white/70">
          <Clock size={11} /> {duration.toFixed(1)}s
        </span>
      )}
    </div>
  </div>
);

const MoveHintSelect = ({ onChange, t }) => (
  <div className="space-y-1.5">
    <label className="font-mono text-[11px] uppercase tracking-wider text-white/45 px-1">{t('moveTypeOptional')}</label>
    <Select defaultValue="auto" onValueChange={onChange}>
      <SelectTrigger className="h-11 rounded-[14px] bg-white/[0.04] border-white/10" data-testid="analyze-move-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-[#1E1E1E] border-white/10 rounded-[16px]">
        <SelectItem value="auto">{t('letAiDetect')}</SelectItem>
        {MOVE_TYPES.filter((m) => m !== 'other').map((m) => (
          <SelectItem key={m} value={m}>{t('moves')[m]}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const UploadZone = ({ onGallery, onCamera, t }) => (
  <div className="ss-card p-6">
    <div className="border-2 border-dashed border-white/15 rounded-[16px] py-12 px-4 flex flex-col items-center gap-4 text-center">
      <span className="w-14 h-14 rounded-[16px] bg-white/[0.07] border border-white/12 flex items-center justify-center">
        <FileVideo size={24} className="text-white" />
      </span>
      <div>
        <h2 className="font-display font-bold text-white">{t('uploadTitle')}</h2>
        <p className="font-mono text-[11px] text-white/45 mt-1">MP4 / MOV / WEBM · ≤ 15s · ≤ 60MB</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2.5 w-full sm:w-auto">
        <button
          onClick={onGallery}
          data-testid="analyze-upload-button"
          className="ss-btn-primary h-11 px-5 text-sm font-semibold flex items-center justify-center gap-2"
        >
          <FileVideo size={16} /> {t('chooseFromGallery')}
        </button>
        <button
          onClick={onCamera}
          data-testid="analyze-record-button"
          className="ss-btn-glass h-11 px-5 text-sm font-medium flex items-center justify-center gap-2"
        >
          <Camera size={16} /> {t('recordNow')}
        </button>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------------- */

export default function Analyze() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [phase, setPhase] = useState('idle'); // idle | selected | uploading | tracking | analyzing | generating | error
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [duration, setDuration] = useState(null);
  const [clientError, setClientError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [serverError, setServerError] = useState(null);

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const pollRef = useRef(null);
  const moveHintRef = useRef('auto');

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetSelection = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setDuration(null);
    setClientError(null);
    setServerError(null);
    setPhase('idle');
    if (galleryInputRef.current) galleryInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleFileSelected = (f) => {
    if (!f) return;
    setClientError(null);
    setServerError(null);
    if (!f.type.startsWith('video/')) {
      setClientError(t('invalidTypeClient'));
      return;
    }
    const url = URL.createObjectURL(f);
    const probe = document.createElement('video');
    probe.preload = 'metadata';
    probe.onloadedmetadata = () => {
      const d = probe.duration;
      setDuration(d);
      if (d && isFinite(d) && d > MAX_DURATION) {
        setClientError(t('videoTooLongClient', { d: d.toFixed(1) }));
        URL.revokeObjectURL(url);
        setFile(null);
        setPreviewUrl(null);
        setPhase('idle');
        return;
      }
      setFile(f);
      setPreviewUrl(url);
      setPhase('selected');
    };
    probe.onerror = () => {
      setDuration(null);
      setFile(f);
      setPreviewUrl(url);
      setPhase('selected');
    };
    probe.src = url;
  };

  const startPolling = (analysisId) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/analyses/${analysisId}/status`);
        if (data.status === 'done') {
          clearInterval(pollRef.current);
          navigate(`/result/${analysisId}?new=1`);
        } else if (data.status === 'failed') {
          clearInterval(pollRef.current);
          setServerError(data.error || t('analysisFailedSub'));
          setPhase('error');
        } else if (['tracking', 'analyzing', 'generating'].includes(data.step)) {
          setPhase(data.step);
        }
      } catch (err) {
        console.warn('Analysis status poll failed (transient, keep polling):', err);
      }
    }, 2500);
  };

  const startAnalysis = async () => {
    if (!file) return;
    setPhase('uploading');
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    if (moveHintRef.current && moveHintRef.current !== 'auto') {
      formData.append('move_type_hint', moveHintRef.current);
    }
    try {
      const { data } = await api.post('/analyses', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setPhase('tracking');
      startPolling(data.analysis_id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : detail || t('analysisFailedSub');
      if (typeof detail === 'object' && detail.code === 'VIDEO_TOO_LONG') {
        setClientError(msg);
        setPhase('selected');
        toast.error(msg);
      } else {
        setServerError(msg);
        setPhase('error');
      }
    }
  };

  const isProcessing = ['uploading', 'tracking', 'analyzing', 'generating'].includes(phase);

  return (
    <div className="space-y-4 max-w-xl mx-auto" data-testid="analyze-page">
      <div className="pt-2">
        <h1 className="font-display font-bold text-2xl tracking-tight text-white">{t('analyzeNewSkill')}</h1>
        <p className="text-sm text-white/55 mt-1">{t('uploadSub')}</p>
      </div>

      {isProcessing ? (
        <ProcessingLoader currentStep={phase} uploadProgress={phase === 'uploading' ? uploadProgress : null} />
      ) : phase === 'error' ? (
        <ErrorCard serverError={serverError} onRetry={resetSelection} t={t} />
      ) : phase === 'selected' && previewUrl ? (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <PreviewCard previewUrl={previewUrl} file={file} duration={duration} onRemove={resetSelection} />
          {clientError && <ClientError text={clientError} />}
          <MoveHintSelect onChange={(v) => { moveHintRef.current = v; }} t={t} />
          <button
            onClick={startAnalysis}
            data-testid="analyze-start-button"
            className="ss-btn-primary w-full py-3.5 font-display font-bold text-base flex items-center justify-center gap-2.5"
          >
            <Upload size={18} />
            {t('startAnalysis')}
          </button>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <UploadZone onGallery={() => galleryInputRef.current?.click()} onCamera={() => cameraInputRef.current?.click()} t={t} />
          {clientError && <ClientError text={clientError} />}
          <input ref={galleryInputRef} type="file" accept="video/*" className="hidden" data-testid="analyze-upload-input" onChange={(e) => handleFileSelected(e.target.files?.[0])} />
          <input ref={cameraInputRef} type="file" accept="video/*" capture="environment" className="hidden" data-testid="analyze-record-input" onChange={(e) => handleFileSelected(e.target.files?.[0])} />
        </motion.div>
      )}
    </div>
  );
}
